import OverflowCell from './OverflowCell';
import rowComparer from './RowComparer';
import PropTypes from 'prop-types';
import { Shimmer, ShimmerElementType } from '@fluentui/react/lib/Shimmer';
import ViewContext from '../Context/ViewContext';
import LoadingContext from '../Context/LoadingContext';
import { useContext } from 'react';
import EditableDataGridContext from '../Context/EditableDataGridContext';
import SalesOrderContext from '../Context/SalesOrderContext';

const React = require('react');
const joinClasses = require('classnames');
const Cell = require('./Cell');
const columnUtils = require('./ColumnUtils');
const cellMetaDataShape = require('./PropTypeShapes/CellMetaDataShape');
const createObjectWithProperties = require('./createObjectWithProperties');
require('styles/react-data-grid/react-data-grid-row.css');

// The list of the propTypes that we want to include in the Row div
const knownDivPropertyKeys = ['height'];

const ShimmerRow = ({ columns, commonRowProps, idx }) => {
  const shimmerElements = columns.flatMap((column, colIdx) => {
    //Semi-randomises the widths of the shimmer elements such that they look more like actual data and feel more natural.

    //Can assume it's numeric if decimal places are provided.
    const isNumeric = !!(column.formatterType === 'numeric' || column.options?.format?.decimals);
    const isSmall = column.width < 100;

    let widthFactor;
    if (isNumeric) {
      widthFactor = 2;
    } else {
      widthFactor = isSmall ? 7 : 2 + ((idx * idx * colIdx) % 5);
    }
    const lineWidth = (column.width / 7) * widthFactor;
    const largeGapWidth = (column.width / 7) * (7 - widthFactor);

    return [
      { type: ShimmerElementType.gap, width: isNumeric ? largeGapWidth : 0 },
      { type: ShimmerElementType.line, height: 8, width: lineWidth - 15 },
      { type: ShimmerElementType.gap, width: (isNumeric ? 0 : largeGapWidth) + 15 },
    ];
  });

  return (
    <div {...commonRowProps}>
      <div className={'react-grid-Cell'} style={{ width: '100%', height: '100%' }}>
        <Shimmer
          shimmerElements={shimmerElements}
          style={{ paddingTop: '10px' }}
          shimmerColors={{
            shimmer: idx % 2 ? '#bababa' : '#c2c2c2', //Slightly darker base colour for the shimmers on odd rows due to the darker background.
            shimmerWave: '#858585',
            background: idx % 2 ? '#f5f5f5' : '#ffffff',
          }}
        />
      </div>
    </div>
  );
};

//Since we're now using 2 context elements, can't use class context (Hooks are required to access multiple context elements in the same component).
const RowWithContext = ({ commonRowProps, rowContent, row, columns, idx }) => {
  const viewContext = useContext(ViewContext);
  const { loading } = useContext(LoadingContext);
  const { hideArchivedLines, hideLinesCondition } = useContext(EditableDataGridContext);

  if (loading || (Object.keys(row).length === 0 && !!viewContext.viewScreenName)) {
    return <ShimmerRow columns={columns} commonRowProps={commonRowProps} idx={idx} />;
  }
  // Don't show order line if archived on a previous save
  if (row.archived && !row.archiveIsNotPersisted && hideArchivedLines) {
    return null;
  }
  if (hideLinesCondition && hideLinesCondition(row)) {
    return null;
  }
  return <div {...commonRowProps}>{rowContent}</div>;
};

class Row extends React.Component {
  static displayName = 'Row';

  static propTypes = {
    height: PropTypes.number.isRequired,
    columns: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
    row: PropTypes.any.isRequired,
    cellRenderer: PropTypes.func,
    cellMetaData: PropTypes.shape(cellMetaDataShape),
    isSelected: PropTypes.bool,
    idx: PropTypes.number.isRequired,
    expandedRows: PropTypes.arrayOf(PropTypes.object),
    extraClasses: PropTypes.string,
    forceUpdate: PropTypes.bool,
    subRowDetails: PropTypes.object,
    isRowHovered: PropTypes.bool,
    colVisibleStart: PropTypes.number.isRequired,
    colVisibleEnd: PropTypes.number.isRequired,
    colDisplayStart: PropTypes.number.isRequired,
    colDisplayEnd: PropTypes.number.isRequired,
    isScrolling: PropTypes.bool.isRequired,
    cellClassName: PropTypes.string,
  };

  static defaultProps = {
    cellRenderer: Cell,
    isSelected: false,
    height: 36,
  };

  shouldComponentUpdate(nextProps) {
    return rowComparer(nextProps, this.props);
  }

  handleDragEnter = () => {
    const handleDragEnterRow = this.props.cellMetaData.handleDragEnterRow;
    if (handleDragEnterRow) {
      handleDragEnterRow(this.props.idx);
    }
  };

  getSelectedColumn = () => {
    if (this.props.cellMetaData) {
      const selected = this.props.cellMetaData.selected;
      if (selected && selected.idx) {
        return columnUtils.getColumn(this.props.columns, selected.idx);
      }
    }
  };

  getCell = (column, i, selectedColumn) => {
    const CellRenderer = this.props.cellRenderer;
    const { colVisibleStart, colVisibleEnd, idx, cellMetaData } = this.props;
    const { key, formatter, locked } = column;
    const baseCellProps = {
      key: `${key}-${idx}-${i}`,
      idx: i,
      rowIdx: idx,
      height: this.getRowHeight(),
      column,
      cellMetaData,
    };

    if ((i < colVisibleStart || i > colVisibleEnd) && !locked && key !== 'id') {
      return <OverflowCell ref={(node) => (this[key] = node)} {...baseCellProps} />;
    }

    const tabIndex = this.context.ignoreKeypresses ? undefined : 0;

    const { row, isSelected, cellClassName } = this.props;
    const cellProps = {
      ref: (node) => (this[key] = node),
      value: this.getCellValue(key || i),
      rowData: row,
      isRowSelected: isSelected,
      expandableOptions: this.getExpandableOptions(key),
      selectedColumn,
      formatter,
      isScrolling: this.props.isScrolling,
      testId: this.props.testId,
      tabIndex,
    };
    return <CellRenderer {...baseCellProps} {...cellProps} className={cellClassName} />;
  };

  getCells = () => {
    const cells = [];
    const lockedCells = [];
    const selectedColumn = this.getSelectedColumn();
    const lastColumnIdx = this.props.columns.length - 1;
    if (this.props.columns) {
      this.props.columns.forEach((column, i) => {
        if (i === lastColumnIdx) {
          column.isLastColumn = true;
        }
        const cell = this.getCell(column, i, selectedColumn);
        if (column.locked) {
          lockedCells.push(cell);
        } else {
          cells.push(cell);
        }
      });
    }

    return cells.concat(lockedCells);
  };

  getRowHeight = () => {
    const rows = this.props.expandedRows || null;
    if (rows && this.props.idx) {
      const row = rows[this.props.idx] || null;
      if (row) {
        return row.height;
      }
    }
    return this.props.height;
  };

  getCellValue = (key) => {
    let val;
    if (key === 'select-row') {
      return this.props.isSelected;
    } else if (typeof this.props.row.get === 'function') {
      val = this.props.row.get(key);
    } else {
      val = this.props.row[key];
    }
    return val;
  };

  isContextMenuDisplayed = () => {
    if (this.props.cellMetaData) {
      const selected = this.props.cellMetaData.selected;
      if (selected && selected.contextMenuDisplayed && selected.rowIdx === this.props.idx) {
        return true;
      }
    }
    return false;
  };

  getExpandableOptions = (columnKey) => {
    const subRowDetails = this.props.subRowDetails;
    if (subRowDetails) {
      return {
        canExpand:
          subRowDetails &&
          subRowDetails.field === columnKey &&
          ((subRowDetails.children && subRowDetails.children.length > 0) || subRowDetails.group === true),
        field: subRowDetails.field,
        expanded: subRowDetails && subRowDetails.expanded,
        children: subRowDetails && subRowDetails.children,
        treeDepth: subRowDetails ? subRowDetails.treeDepth : 0,
        subRowDetails: subRowDetails,
      };
    }
    return {};
  };

  setScrollLeft = (scrollLeft) => {
    this.props.columns.forEach((column) => {
      if (column.locked) {
        if (!this[column.key]) return;
        this[column.key].setScrollLeft(scrollLeft);
      }
    });
  };

  getKnownDivProps = () => {
    return createObjectWithProperties(this.props, knownDivPropertyKeys);
  };

  render() {
    //Note - 'row-archived-workspace' does not yet exist, just providing the option to style archived rows at a later time.
    const rowArchiveClassName =
      this.props.cellMetaData.gridType === 'workspace' ? 'row-archived-workspace' : 'row-archived';

    const className = joinClasses(
      'react-grid-Row',
      `react-grid-Row--${this.props.idx % 2 === 0 ? 'even' : 'odd'}`,
      {
        'row-selected': this.props.isSelected,
        //Not checked, but user has clicked on the row to edit.
        'row-soft-selected':
          this.props.cellMetaData.selected.rowIdx === this.props.idx && this.props.cellMetaData.enableCellSelect,
        'row-context-menu': this.isContextMenuDisplayed(),
        [rowArchiveClassName]: this.props.row.archived,
        'row-deleted': this.props.row.deleted,
      },
      this.props.extraClasses
    );

    const style = {
      height: this.getRowHeight(this.props),
      overflow: 'hidden',
      contain: 'layout',
    };

    const commonRowProps = {
      'data-testid': `${this.props.testId}-row-${this.props.idx}`,
      ...this.getKnownDivProps(),
      className: className,
      style: style,
    };

    const rowContent = React.isValidElement(this.props.row) ? this.props.row : this.getCells();

    const { columns, row, idx } = this.props;

    return (
      <RowWithContext
        commonRowProps={commonRowProps}
        onDragEnter={this.handleDragEnter}
        rowContent={rowContent}
        columns={columns}
        row={row}
        idx={idx}
      />
    );
  }
}

Row.contextType = EditableDataGridContext;

export default Row;
